import { gate } from 'hub-http/gates';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { UnifiedReportResolver } from './UnifiedReportResolver';
import { UnifiedValidationErrorBoundary } from './UnifiedValidationErrorBoundary';
import { getValidatedReportTrackingInfo } from './unifiedVizValidationTrackingUtils';
import { Metrics } from '../../../monitoring/Metrics';
import { UNIFIED_VIZ_RESOLVE_VALIDATION } from '../../../constants/trackingMetrics';
import { convertMetricDimensionPropertiesToStrings } from 'reporting-data/utils/metricsUtils';
import memoize from 'transmute/memoize';
import { getMigratedReport } from './migration';
import { ReportingLogger } from 'reporting-data/monitoring/reportingLogger';
//@ts-expect-error untyped dependency
import { stableStringify } from 'reporting-data/lib/stringify';
import { isSlicedVisualization } from 'reporting-data/tsTypes/visualization';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const sanitizedVizString = val => {
  const queryIdRegex = /"queryId":"[^"]+",?/;
  const dataAgeRegex = /"dataAge":\d+,?/;
  return val.replace(queryIdRegex, '').replace(dataAgeRegex, '');
};
const sanitizeVizRecordOfData = val => {
  const vizWithNoData = val.set('data', null);
  let vizWithNoDerivedData = vizWithNoData;
  // TODO: account for other visualization types in the future
  if (isSlicedVisualization(val)) {
    vizWithNoDerivedData = vizWithNoData.setIn(['layer', 'color', 'defined'], null).setIn(['layer', 'color', 'definedPatterns'], null).setIn(['layer', 'color', 'order'], null);
    return vizWithNoDerivedData;
  }
  return vizWithNoData;
};
export const memoizedStringify = memoize(val => {
  if (val) {
    const vizWithNoData = sanitizeVizRecordOfData(val);
    return {
      // raw viz value
      value: JSON.stringify(val),
      // sanitized viz value without things that we do not want to compare ie: queryId, dataAge
      sanitized: sanitizedVizString(JSON.stringify(val)),
      // sanitized viz value with no data (This is being used if we want to compare the viz objects with no data)
      sanitizedWithNoData: vizWithNoData && sanitizedVizString(JSON.stringify(vizWithNoData))
    };
  }
  return {};
});
const isVizEqual = (stringifiedLegacyViz, stringifiedUnifiedViz) => {
  return stringifiedLegacyViz === stringifiedUnifiedViz;
};
const useVisualizationValidation = ({
  legacyVisualization,
  legacyReportResolveError,
  unifiedVisualization,
  unifiedReportResolveError,
  validatedReportTrackingInfo,
  reportMigrationSuccess,
  reportMigrationError,
  report,
  migratedReport,
  debug
}) => {
  const logger = useMemo(() => new ReportingLogger(), []);

  // TODO: there might be upstream perfmance issue here with the legacy flow. The value of the legacyVisualization is not stable.
  const stringifiedLegacyViz = memoizedStringify(legacyVisualization);
  const stringifiedUnifiedViz = memoizedStringify(unifiedVisualization);
  const metricsJsValidationDimensions = useMemo(() => {
    const isSupportedReportType = validatedReportTrackingInfo === null || validatedReportTrackingInfo === void 0 ? void 0 : validatedReportTrackingInfo.isUnifiedVizSupportedReportType;
    const hasLegacyReportResolveError = !!legacyReportResolveError;
    const hasUnifiedReportResolveError = !!unifiedReportResolveError;
    const isLegacyResolveComplete = stringifiedLegacyViz.value || hasLegacyReportResolveError;
    const isUnifiedResolveComplete = stringifiedUnifiedViz.value || hasUnifiedReportResolveError;
    const incompleteResolve = isSupportedReportType && !isLegacyResolveComplete || reportMigrationSuccess && !isUnifiedResolveComplete;
    if (validatedReportTrackingInfo === undefined || incompleteResolve) {
      return;
    }
    const hasStringifiedLegacyViz = !!stringifiedLegacyViz.value;
    const hasStringifiedUnifiedViz = !!stringifiedUnifiedViz.value;
    let areVizObjectsEqual = false;
    let areVizObjectsEqualWithNoData = false;
    if (hasStringifiedLegacyViz && hasStringifiedUnifiedViz && !hasLegacyReportResolveError && !hasUnifiedReportResolveError) {
      areVizObjectsEqual = isVizEqual(stringifiedLegacyViz.sanitized, stringifiedUnifiedViz.sanitized);
      areVizObjectsEqualWithNoData = isVizEqual(stringifiedLegacyViz.sanitizedWithNoData, stringifiedUnifiedViz.sanitizedWithNoData);
    }
    return Object.assign({}, validatedReportTrackingInfo, {
      areVizObjectsEqual,
      areVizObjectsEqualWithNoData,
      legacyVizResolveSuccess: !!isLegacyResolveComplete && legacyReportResolveError === undefined,
      unifiedVizResolveSuccess: !!isUnifiedResolveComplete && unifiedReportResolveError === undefined,
      reportMigrationSuccess
    });
  }, /* eslint-disable react-hooks/exhaustive-deps */
  [legacyReportResolveError, reportMigrationSuccess, stringifiedLegacyViz.value, stringifiedUnifiedViz.value, unifiedReportResolveError, validatedReportTrackingInfo]);
  useEffect(() => {
    // metricsJsValidationDimensions isn't populated until the resolve process is complete
    // with either errors or results. So we'll wait for that before logging.
    //
    // If there is a legacy report resolve error, this is an immediate customer issue
    // and will be tracked and handled outside of the unified viz validation context.
    // No need to track this is a validation failure.
    if (metricsJsValidationDimensions && !legacyReportResolveError) {
      // if there is no reportMigrationError and migratedReport, that means that we do not run the unified viz migration
      // in that case we should not send logging
      const shouldSendLogging = !metricsJsValidationDimensions.areVizObjectsEqual && (reportMigrationError || migratedReport);
      // areVizObjectsEqual is false if the viz objects are not equal or if an error occurred when
      // generating either of the viz objects.
      const stringifiedReport = stableStringify(report);
      const stringifiedMigratedReport = migratedReport && stableStringify(migratedReport);
      if (shouldSendLogging) {
        logger.addAttributes(Object.assign({}, metricsJsValidationDimensions, {
          stringifiedLegacyViz: stringifiedLegacyViz.value,
          stringifiedUnifiedViz: stringifiedUnifiedViz.value,
          legacyReportResolveError,
          unifiedReportResolveError,
          reportMigrationError,
          report: stringifiedReport,
          migratedReport: stringifiedMigratedReport
        }));
        logger.sendPageAction('ReportingUnifiedVizValidation');
      }
      if (debug) {
        debug.set('UnifiedVizValidation debug', Object.assign({}, metricsJsValidationDimensions, {
          stringifiedLegacyViz,
          stringifiedUnifiedViz,
          originalReport: stringifiedReport,
          migratedReport: stringifiedMigratedReport
        }));
      }
      Metrics.counter(UNIFIED_VIZ_RESOLVE_VALIDATION, convertMetricDimensionPropertiesToStrings(metricsJsValidationDimensions)).increment();
    }
  }, [legacyReportResolveError, logger, metricsJsValidationDimensions, stringifiedLegacyViz.value, stringifiedUnifiedViz.value, unifiedReportResolveError, reportMigrationError, report, migratedReport]);
};
const VALIDATE_UNIFIED_VIZ_GATE = gate('ReportingViz:ValidateUnifiedVizResolve');
const useValidateUnifiedVizEnabled = () => {
  const userInfo = useContext(UserInfoContext);
  return useMemo(() => userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates.includes(VALIDATE_UNIFIED_VIZ_GATE), [userInfo]);
};
export const UnifiedVizValidationContext = /*#__PURE__*/createContext({
  setLegacyVisualization: () => {},
  setUnifiedVisualization: () => {},
  setUnifiedReportResolveError: () => {},
  setLegacyReportResolveError: () => {}
});
const UnifiedVizValidationContextProvider = ({
  children,
  report,
  debug,
  isMobile
}) => {
  const [legacyVisualization, setLegacyVisualization] = useState();
  const [legacyReportResolveError, setLegacyReportResolveError] = useState();
  const [unifiedVisualization, setUnifiedVisualization] = useState();
  const [unifiedReportResolveError, setUnifiedReportResolveError] = useState();
  const [migratedReport, setMigratedReport] = useState();
  const [reportMigrationError, setReportMigrationError] = useState();
  const validatedReportTrackingInfo = getValidatedReportTrackingInfo(report);
  const {
    gates
  } = useContext(UserInfoContext);
  useEffect(() => {
    try {
      const reportMigrated = getMigratedReport(report, gates);
      setMigratedReport(reportMigrated);
    } catch (error) {
      setReportMigrationError(error);
    }
  }, [report, gates]);
  useVisualizationValidation({
    legacyVisualization,
    legacyReportResolveError,
    unifiedVisualization,
    unifiedReportResolveError,
    validatedReportTrackingInfo,
    reportMigrationSuccess: !!migratedReport && reportMigrationError === undefined,
    reportMigrationError,
    report,
    migratedReport,
    debug
  });
  const providerValue = useMemo(() => ({
    setLegacyVisualization,
    setLegacyReportResolveError,
    setUnifiedVisualization,
    setUnifiedReportResolveError
  }), []);
  return /*#__PURE__*/_jsxs(UnifiedVizValidationContext.Provider, {
    value: providerValue,
    children: [children, migratedReport ? /*#__PURE__*/_jsx(UnifiedValidationErrorBoundary, {
      onError: setUnifiedReportResolveError,
      children: /*#__PURE__*/_jsx(UnifiedReportResolver, {
        report: migratedReport,
        validationMode: true,
        isMobile: isMobile
      })
    }) : undefined]
  });
};
export const MaybeUnifiedVizValidationContextProvider = ({
  children,
  report,
  debug,
  isMobile,
  disableUnifiedVizValidation
}) => {
  const validateUnifiedVizEnabled = useValidateUnifiedVizEnabled();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [unifiedValidationError, setUnifiedValidationError] = useState();
  return !disableUnifiedVizValidation && validateUnifiedVizEnabled ? /*#__PURE__*/_jsx(UnifiedValidationErrorBoundary, {
    onError: setUnifiedValidationError,
    fallback: /*#__PURE__*/_jsx(_Fragment, {
      children: children
    }),
    children: /*#__PURE__*/_jsx(UnifiedVizValidationContextProvider, {
      report: report,
      debug: debug,
      isMobile: isMobile,
      children: children
    })
  }) : /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};